<template>
  <div class="relative">
    <div class="overlay-disable-setting" v-if="isDisableEditPresentation"></div>
    <aha-checkbox
      id="settings-check-audience-admission"
      @change="handleCheckBoxAudienceAuthentication"
      v-model="presentation.isAudienceAuthentication"
      :disabled="!isOwnerOfPresentation"
      class="mb-3"
      style="font-size: 13.5px"
    >
      <span class="-mr-4 -ml-1">
        {{ $i18nPresenter.t('Require participants to enter authentication code') }}
        <AhaSvg
          style="width: 14px; margin-bottom: 3px;"
          class="text-base-70"
          name="question"
          v-tooltip="audienceAuthenticationTooltip"
        />
      </span>
    </aha-checkbox>
    <div
      v-if="presentation.isAudienceAuthentication && isOwnerOfPresentation"
    >
      <div class="flex-row flex">
        <aha-input
          type="number"
          class="w-48"
          :placeholder="$i18nPresenter.t('Number of participants')"
          required
          v-model="presentation.numberOfAuthens"
          @change="updateNumberOfAuthen()"
          @input="debounceNumberOfAuthen()"
        />
        <aha-button
          class="ml-2"
          @click="generateListCode()"
          :disabled="isSubmitGenerate || !presentation.numberOfAuthens"
        >
          {{ $i18nPresenter.t('Generate code') }} <aha-spinner v-if="isSubmitGenerate" class="ml-2" />
        </aha-button>
      </div>

      <div class="inline-block">
        <a
          @click="downloadFile()"
          class="text-primary-90 cursor-pointer mt-4 flex flex-row"
        >
          {{ $i18nPresenter.t('Download authentication code list') }}
          <aha-spinner v-if="isSubmitGetList" class="ml-2 block" />
        </a>
      </div>
      <br />
      <div class="inline-block">
        <a
          @click="isShowDeleteCode = true"
          class="text-danger-90 cursor-pointer mt-2 flex flex-row underline"
        >
          {{ $i18nPresenter.t('Delete generated codes') }}
        </a>
      </div>
    </div>
    <aha-modal
      :title="$i18nPresenter.t('Delete generated codes')"
      id="modal-confirm-delete-generate-code"
      v-if="isShowDeleteCode"
      @hide="isShowDeleteCode = false"
    >
      <div class="overflow-hidden">
        {{ $i18nPresenter.t('All generated codes will no longer be valid after being deleted.') }}
      </div>
      <template #action>
        <aha-link @click="isShowDeleteCode = false">
          <span class="font-bold">{{ $i18nPresenter.t('Cancel') }}</span>
        </aha-link>
        <aha-button
          variant="danger"
          class="mr-2"
          @click="deleteCode()"
          has-margin-left
          :disabled="isSubmitDeleteList"
          :submitting="isSubmitDeleteList"
        >
          {{ $i18nPresenter.t('Delete') }}
        </aha-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import _ from 'lodash'

import { mapState, mapActions } from 'vuex'
import utils from '@/util'
import config from '@/config'

import PresentationApi from '@/api/presentation'
import eventCategories from '@/constant/eventCategories'
import audienceAuthenticationApi from '@/api/audienceAuthentication'

export default {
  components: {
  },
  data() {
    return {
      numberOfPaticipants: 10,
      isSubmitGenerate: false,
      isSubmitGetList: false,
      isSubmitDeleteList: false,
      isShowDeleteCode: false,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      currentUser: (state) => state.currentUser,
    }),
    isEURegion() {
      return config.region === 'eu'
    },
    audienceAuthenticationTooltip() {
      return this.$i18nPresenter.t('Participants must enter the unique codes you provide, to be able to join the presentation.')
    },
    isOwnerOfPresentation() {
      if (this.currentUser.id === this.presentation.userId) {
        return true
      }
      return false
    },
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'updatePresentation']),

    // eslint-disable-next-line func-names
    debounceNumberOfAuthen: _.debounce(async function () {
      this.updateNumberOfAuthen()
    }, 300),

    async handleCheckBoxAudienceAuthentication() {
      await this.updateAdditionalFields()
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Audience authentication > Audience authentication checkbox',
        `Audience Authentication: ${this.presentation.isAudienceAuthentication}`,
      )
    },
    trackingUpgrade() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Audience authentication > Audience authentication button upgrade',
        'Audience Authentication Click Upgrade',
      )
    },
    async updateAdditionalFields() {
      try {
        this.setSyncLoadingAction(1)
        await PresentationApi.updatePresentationByPresentationId(this.presentation.id, {
          isAudienceAuthentication: this.presentation.isAudienceAuthentication,
        })
        this.updatePresentation(this.presentation)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },
    async updateNumberOfAuthen() {
      try {
        if (this.presentation.numberOfAuthens > 2000) {
          this.presentation.numberOfAuthens = 2000
        }
        if (this.presentation.numberOfAuthens < 0 || !this.presentation.numberOfAuthens) {
          this.presentation.numberOfAuthens = 0
        }
        this.setSyncLoadingAction(1)
        await PresentationApi.updatePresentationByPresentationId(this.presentation.id, {
          numberOfAuthens: this.presentation.numberOfAuthens,
        })
        this.updatePresentation(this.presentation)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },
    async generateListCode() {
      if (this.isSubmitGenerate) return
      this.isSubmitGenerate = true
      try {
        const respAudiences = await audienceAuthenticationApi.createAudienceAuthentication({
          presentationId: this.presentation.id,
          numberOfPaticipants: parseInt(this.presentation.numberOfAuthens, 10),
        })
        this.getFile(respAudiences)
        utils.showToastSuccess(this.$i18nPresenter.t('Authentication code generated successfully'))
      } catch {
        // do nothing
      }
      this.isSubmitGenerate = false
    },
    async deleteCode() {
      if (this.isSubmitDeleteList) return
      this.isSubmitDeleteList = true
      try {
        await audienceAuthenticationApi.deleteAudienceAuthentication({
          presentationId: this.presentation.id,
        })
        this.isShowDeleteCode = false
        utils.showToastSuccess(this.$i18nPresenter.t('Delete generated code successfully'))
      } catch {
        // do nothing
      }
      this.isSubmitDeleteList = false
    },
    async downloadFile() {
      if (this.isSubmitGetList) return
      this.isSubmitGetList = true
      const resp = await audienceAuthenticationApi.getAudienceAuthentication({
        presentationId: this.presentation.id,
      })
      this.getFile(resp)
      this.isSubmitGetList = false
    },

    getFile(data) {
      const csv = data.map((d) => {
        return `${d.authenticationCode}\n`
      }).toString().replaceAll(',', '')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      // Create a link to download it
      const pom = document.createElement('a')
      pom.href = url
      pom.setAttribute('download', 'ahaslides-participant-code.csv')
      pom.click()
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-setting {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full z-20;
}
</style>
